import { GTooltip } from 'components/basic-blocks'
import { useMemo } from 'react'
import { NavLink, useLocation, useMatch } from 'react-router-dom'
import { useUiStore } from 'store'
import { classNames } from '../../../utils'
import { NavButtonProps, NavItemProps } from './types'
import { GBadge } from '../../../components/basic-blocks/g-badge'
import { appendMarketingParams } from '../../../utils/marketing-params'

const SHARED_PARAMS = [
	'from', 'to',
	'platforms',
	'adAccounts',
	'campaigns',
	'breadCampaigns'
]

const isFilterPage = (path: string): boolean => {
	const filterPages = ['dashboard', 'analytics', 'clicks', 'threat', 'exclusions', 'blacklist', 'reports', 'protection']
	const normalizedPath = path.replace(/^\/+|\/+$/g, '')
	const pathParts = normalizedPath.split('/').filter(Boolean)
	const hasWorkspace = pathParts.includes('workspace')
	const hasAsset = pathParts.includes('asset')

	if (!hasWorkspace || !hasAsset) {
		return false
	}
	const assetIndex = pathParts.indexOf('asset')
	if (assetIndex !== -1) {
		const pageAfterAsset = pathParts[assetIndex + 2]
		if (!pageAfterAsset) {
			return true
		}
		return filterPages.includes(pageAfterAsset.toLowerCase())
	}

	return false
}

const getSharedSearchParams = (search: string): string => {
	const source = new URLSearchParams(search)
	const shared = new URLSearchParams()
	source.forEach((value, key) => {
		if (SHARED_PARAMS.includes(key)) {
			shared.set(key, value)
		}
	})
	const result = shared.toString()
	return result ? `?${result}` : ''
}

export const NavItem = (props: NavItemProps) => {
	const location = useLocation()
	const { pathname } = location
	const desktopSidebarCollapsed = useUiStore((s) => s.desktopSidebarCollapsed)

	const isPartialActive = useMemo(
		() => (props.activeText ? pathname.includes(props.activeText) : false),
		[props.activeText, pathname]
	)

	const match = useMatch({
		path: props.to()
	})

	if (props.tooltip) {
		return (
			<GTooltip content={props.tooltip}>
				{renderNavLinkView(
					props,
					!!match,
					desktopSidebarCollapsed,
					isPartialActive,
					location
				)}
			</GTooltip>
		)
	}

	return renderNavLinkView(
		props,
		!!match,
		desktopSidebarCollapsed,
		isPartialActive,
		location
	)
}

const renderNavLinkView = (
	props: NavItemProps,
	match: boolean,
	collapsed: boolean,
	isActive: boolean,
	location: ReturnType<typeof useLocation>
) => {
	const currentSearch = location.search
	const currentPath = location.pathname
	const targetPath = props.to()
	const isNavigatingBetweenFilterPages = 
		isFilterPage(currentPath) && isFilterPage(targetPath)
	
	let finalPath: string
	if (props.disabled) {
		finalPath = '#'
	} else if (currentSearch) {
		const sharedSearch = getSharedSearchParams(currentSearch)
		finalPath = `${targetPath}${sharedSearch}`
		finalPath = appendMarketingParams(finalPath)
	} else {
		finalPath = appendMarketingParams(targetPath)
	}
	
	return (
		<NavLink
			to={finalPath}
			onClick={props.onClick}
			className={classNames(
				isActive || match ? 'bg-t-google-btn text-t-google-ad' : '',
				props.disabled
					? 'text-t-tertiary'
					: 'text-t-google-tooltip hover:text-t-google-ad',
				props.nested ? 'pl-12' : '',
				'group flex font-inter items-center px-3 py-2 text-sm rounded-3xl',
				props.className ? props.className : ''
			)}
		>
			{props?.icon && (
				<>
					{collapsed ? (
						<GTooltip
							content={props.label}
							contentClassName="bg-t-smoke border-none text-t-desc-gray"
							position="right"
						>
							<props.icon
								className={classNames(
									isActive || match
										? props.activeIconClass || 'text-t-email-blue'
										: '',
									'flex-shrink-0 h-[24px] w-[24px]'
								)}
								aria-hidden="true"
							/>
						</GTooltip>
					) : (
						<props.icon
							className={classNames(
								isActive || match
									? props.activeIconClass || 'text-t-email-blue'
									: '',
								'flex-shrink-0 h-[20px] w-[20px]'
							)}
							aria-hidden="true"
						/>
					)}
				</>
			)}
			{!collapsed && (
				<div className="flex items-center gap-1">
					<span
						className={`ml-3 text-base font-medium  ${
							(isActive || match) && 'text-t-google-ad'
						}`}
					>
						{props.label}
					</span>
					{props?.badge && (
						<GBadge
							text={props.badge.text}
							color={props.badge.color}
							className={props.badge?.className}
						/>
					)}
				</div>
			)}
		</NavLink>
	)
}

export const NavButtonItem = ({
	nested,
	className,
	tooltip,
	disabled,
	icon: Icon,
	onClick,
	badge,
	label
}: NavButtonProps) => {
	const collapsed = useUiStore((s) => s.desktopSidebarCollapsed)

	return (
		<GTooltip content={tooltip ?? ''}>
			<div
				onClick={onClick}
				className={classNames(
					disabled
						? 'text-t-secondary pointer-events-none'
						: 'text-t-menu hover:bg-gray-50 cursor-pointer',
					nested ? 'pl-12' : '',
					'group flex font-bold items-center px-3 py-2 text-sm rounded-md',
					className ? className : ''
				)}
			>
				{Icon && (
					<>
						{collapsed ? (
							<GTooltip content={label} position="right">
								<Icon
									className={classNames('flex-shrink-0 h-[24px] w-[24px]')}
									aria-hidden="true"
								/>
							</GTooltip>
						) : (
							<Icon
								className={classNames('flex-shrink-0 h-[20px] w-[20px]')}
								aria-hidden="true"
							/>
						)}
					</>
				)}
				{!collapsed && (
					<div className="flex items-center gap-1">
						<span className={'ml-3 text-lg'}>{label}</span>
						{badge && (
							<GBadge
								text={badge.text}
								color={badge.color}
								className={badge?.className}
							/>
						)}
					</div>
				)}
			</div>
		</GTooltip>
	)
}
